import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtom } from "jotai";
import { readInlineData, useFragment } from "react-relay";

import {
  DropdownSelectOption,
  MultiSelectCheckbox,
} from "@whoosh/design-system/src/components/controls";
import { VStack, Text } from "@whoosh/design-system/src/components/primitive";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";

import { courseFilterAtom } from "../atoms/courseFilterAtom";
import { DepartmentAvailabilityCoursePicker_department$key } from "./__generated__/DepartmentAvailabilityCoursePicker_department.graphql";
import { DepartmentAvailabilityCoursePicker_facilityToCourseOption$key } from "./__generated__/DepartmentAvailabilityCoursePicker_facilityToCourseOption.graphql";

type Props = {
  departmentRef: DepartmentAvailabilityCoursePicker_department$key | null;
};
export const DepartmentAvailabilityCoursePicker = ({
  departmentRef,
}: Props) => {
  const [selectedCourses, setSelectedCourses] = useAtom(courseFilterAtom);

  const department = useFragment(
    DepartmentAvailabilityCoursePickerFragmentString,
    departmentRef
  );

  const golfFacilities =
    department?.facilities?.edges?.flatMap((e) =>
      e?.node && e.node.type === "GOLF_COURSE" ? [e.node] : []
    ) || [];

  const courseOptions =
    golfFacilities.flatMap((facility) =>
      facility ? facilityToCourseOptions(facility) : []
    ) || [];
  const courseNoun = getNounOverride(
    golfFacilities[0]?.copyOverrides?.course?.plural,
    t`Courses`
  );

  const onChange = (values: (string | null)[]) => {
    const selected = values.flatMap((v) => (v ? [v] : []));
    setSelectedCourses(selected);
  };

  return (
    <VStack gap={4}>
      <Text variant="mainSmallSemiBold">{courseNoun}</Text>
      <MultiSelectCheckbox
        placeholder={t`All ${courseNoun}`}
        showClearAll
        values={selectedCourses}
        options={courseOptions}
        onChange={onChange}
      />
    </VStack>
  );
};

const DepartmentAvailabilityCoursePickerFragmentString = graphql`
  fragment DepartmentAvailabilityCoursePicker_department on Department {
    id
    facilities {
      edges {
        node {
          type
          copyOverrides {
            course {
              plural
            }
          }
          ...DepartmentAvailabilityCoursePicker_facilityToCourseOption
        }
      }
    }
  }
`;

const facilityToCourseOptions = (
  facilityRef: DepartmentAvailabilityCoursePicker_facilityToCourseOption$key
): DropdownSelectOption<string>[] => {
  const facility = readInlineData(
    facilityToCourseOptionFragmentString,
    facilityRef
  );

  const isSingleCourseFacility = facility?.courses?.count === 1;
  const courses =
    facility?.courses?.edges?.flatMap((e) => (e?.node ? [e.node] : [])) || [];

  return courses.map((course) => ({
    label: isSingleCourseFacility
      ? facility.name
      : `${facility.name} - ${course.name}`,
    value: course.id,
  }));
};

const facilityToCourseOptionFragmentString = graphql`
  fragment DepartmentAvailabilityCoursePicker_facilityToCourseOption on Facility
  @inline {
    name
    courses(archived: false) {
      count
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;
